import { useEffect } from 'react';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// ---------------------------------------------------------

import Icon from '@components/icon';
import Link from '@components/link';
import Image from '@components/image';
import { pushDataLayerEvent } from '@lib/gtm';

// ---------------------------------------------------------

import styles from './styles.module.scss';
const {
  bg_container,
  bg_overlay,
  card,
  contact_content,
  contact_link,
  container,
  full_width,
  inner_content,
  on_listing_page,
  realtor_card_horizontal_large,
  realtor_card_horizontal_small,
  realtor_card_horizontal_small_stacked,
  realtor_card_office_detail,
  realtor_card_primary,
  realtor_img,
  realtor_licenses,
  realtor_phone,
  realtor_plain,
  realtor_subtitle,
  realtor_title
} = styles;

const themeOptions = {
  horizontalLarge: realtor_card_horizontal_large,
  horizontalSmall: realtor_card_horizontal_small,
  horizontalSmallStacked: realtor_card_horizontal_small_stacked,
  officeDetail: realtor_card_office_detail,
  primary: realtor_card_primary
};

// ---------------------------------------------------------

const RealtorCard = (props) => {
  const {
    contact = {},
    hasTour = false,
    imageDimensions = { height: 732, width: 528 },
    onListingPage = false,
    setModalIsOpen = () => {},
    showLicensedIn = false,
    theme = 'primary'
  } = props;

  const {
    agentLandingPageUrl,
    designations,
    displayEmail,
    displayPhone,
    displayPhotoUrl,
    displayPrimary,
    displaySecondary,
    displayWebsiteUrl,
    isIndianaLicensed,
    isKentuckyLicensed,
    isOhioLicensed,
    languages,
    agentId
  } = contact;

  const router = useRouter();

  const containerClasses = classNames(container, {
    [full_width]: onListingPage
  });

  const classes = classNames({
    [on_listing_page]: onListingPage,
    [themeOptions[theme]]: themeOptions[theme]
  });

  useEffect(() => {
    if (hasTour && router?.isReady) {
      setModalIsOpen(!!router?.query?.tourModal);
    }
  }, [router]);

  const openTourModal = () => {
    if (hasTour) {
      setModalIsOpen(true);
      const currentUrl = router.asPath;
      const urlWithoutQuery = currentUrl.split('?')[0];

      return router.replace(
        { pathname: urlWithoutQuery, query: { tourModal: agentId } },
        undefined,
        {
          shallow: true
        }
      );
    }
  };

  const licenseString = () => {
    const licenses = [];
    if (isOhioLicensed) licenses.push('OH');
    if (isKentuckyLicensed) licenses.push('KY');
    if (isIndianaLicensed) licenses.push('IN');
    return licenses.length ? `Licensed in ${licenses.join(', ')}` : '';
  };

  const isLargeTheme = theme === 'horizontalLarge' || theme === 'officeDetail';

  const contactLinks = (
    <div className={contact_content}>
      {onListingPage ? (
        <>
          {displayPhone && (
            <span className={contact_link}>
              <Link url={`tel:${displayPhone}`} dataLayerEvent="listing_call_click">
                <Icon name="phone" type="actions" />
                Call
              </Link>
            </span>
          )}
          {displayPhone && (
            <span className={contact_link}>
              <Link url={`sms:${displayPhone}`} dataLayerEvent="listing_text_click">
                <Icon name="text" type="actions" />
                Text
              </Link>
            </span>
          )}
          {hasTour ? (
            <span className={contact_link}>
              <button
                onClick={() => {
                  pushDataLayerEvent('listing_request_a_tour_modal_click', router.asPath);
                  openTourModal();
                }}
              >
                <Icon type="general" name="clock" />
                Tour
              </button>
            </span>
          ) : (
            displayWebsiteUrl && (
              <span className={contact_link}>
                <Link url={displayWebsiteUrl} dataLayerEvent="listing_website_click">
                  <Icon name="website" type="actions" />
                  Website
                </Link>
              </span>
            )
          )}
        </>
      ) : (
        <>
          {displayEmail && (
            <span className={contact_link}>
              <Link url={`mailto:${displayEmail}`}>
                <Icon name="email" type="actions" />
                Email
              </Link>
            </span>
          )}
          {displayPhone && (
            <span className={contact_link}>
              <Link url={`tel:${displayPhone}`}>
                <Icon name="phone" type="actions" />
                Call
              </Link>
            </span>
          )}
          {displayWebsiteUrl && (
            <span className={contact_link}>
              <Link url={displayWebsiteUrl}>
                <Icon name="website" type="actions" />
                Website
              </Link>
            </span>
          )}
        </>
      )}
    </div>
  );

  return (
    <div className={containerClasses}>
      <div className={classes}>
        <div className={bg_container}>
          <div className={bg_overlay}></div>
        </div>
        <div className={card}>
          <Link
            url={agentLandingPageUrl ?? displayWebsiteUrl}
            className={realtor_img}
            dataLayerEvent={onListingPage ? 'listing_agent_click' : undefined}
          >
            <Image
              image={{
                alt: `Headshot of ${displayPrimary}`,
                height: imageDimensions?.height ?? 125,
                src: displayPhotoUrl ? displayPhotoUrl : '/images/avatar_placeholder.jpeg',
                width: imageDimensions?.width ?? 165
              }}
              layout="responsive"
            />
          </Link>
          <div className={inner_content}>
            <h2 className={realtor_title}>
              <Link
                url={agentLandingPageUrl ?? displayWebsiteUrl}
                dataLayerEvent={onListingPage ? 'listing_agent_click' : undefined}
              >
                {displayPrimary}
              </Link>
            </h2>

            <h3 className={realtor_subtitle}>{displaySecondary}</h3>
            <div className={realtor_phone}>
              <Link url={`tel:${displayPhone}`} dataLayerEvent="listing_call_click">
                {displayPhone}
              </Link>
            </div>
            {designations && !onListingPage && <div className={realtor_plain}>{designations}</div>}
            {theme !== 'horizontalSmall' && theme !== 'horizontalSmallStacked' && languages && (
              <div className={realtor_plain}>Also speaks {languages}</div>
            )}
            {showLicensedIn && licenseString() && (
              <div className={realtor_licenses}>{licenseString()}</div>
            )}
          </div>
          {isLargeTheme && contactLinks}
        </div>
        {!isLargeTheme && contactLinks}
      </div>
    </div>
  );
};

RealtorCard.propTypes = {
  /**
   * Controls the layout of the realtor card.
   */
  theme: PropTypes.oneOf(Object.keys(themeOptions)),

  /**
   * The dimensions of the image for the realtor.
   */
  imageDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }),

  /**
   * Specifies if the listing has the request a tour
   */
  hasTour: PropTypes.bool,

  /**
   * The contact information for the realtor.
   */
  contact: PropTypes.shape({
    agentLandingPageUrl: PropTypes.string,
    designations: PropTypes.string,
    displayEmail: PropTypes.string,
    displayPhone: PropTypes.string,
    displayPhotoUrl: PropTypes.string,
    displayPrimary: PropTypes.string,
    displaySecondary: PropTypes.string,
    displayWebsiteUrl: PropTypes.string,
    individualPhotoUrl: PropTypes.string,
    isIndianaLicensed: PropTypes.bool,
    isKentuckyLicensed: PropTypes.bool,
    isOhioLicensed: PropTypes.bool,
    languages: PropTypes.string
  }),

  /**
   * Specifies if component renders on listing page (changes styles)
   */
  onListingPage: PropTypes.bool,

  /**
   * Function to open the request a tour modal
   */
  setModalIsOpen: PropTypes.func,

  /**
   * Specifies if the licensed in string should be shown
   */
  showLicensedIn: PropTypes.bool
};

export default RealtorCard;
